import debounce from 'debounce';

/**
 * Shared AudioContext.
 *
 * OWA-671: this used to be constructed per call and never closed. Browsers cap
 * concurrent AudioContexts at roughly six, so after a handful of task
 * completions every later sound failed silently — the leak presented as a
 * feature that simply stopped working, with nothing in the console.
 *
 * A shared context is preferred over closing one per sound: close() during
 * playback cuts the sound off, and the element must stay referenced until it
 * finishes or it can be collected mid-play.
 */
let sharedContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
    if (!sharedContext) sharedContext = new AudioContext();

    return sharedContext;
}

/**
 * Play a sound file.
 *
 * @param {string} sound
 * @param {number} gain
 */
function _playSound(sound: string, gain: number = 1) {
    try {
        const audio = new Audio('/audio/' + sound + '.mp3');
        const audioContext = getAudioContext();
        const gainNode = audioContext.createGain();

        // Safe with a shared context: createMediaElementSource may only be
        // called once per <audio> element, and this element is new each call.
        const track = audioContext.createMediaElementSource(audio);
        track.connect(gainNode).connect(audioContext.destination);
        gainNode.gain.value = gain;

        // A context created inside a user gesture starts running, but a shared
        // one can be suspended later by autoplay policy, a backgrounded tab or
        // an iOS interruption. Without this every subsequent sound would be
        // silent for the rest of the session.
        const ready = audioContext.state === 'suspended'
                      ? audioContext.resume()
                      : Promise.resolve();

        ready
            .then(() => audio.play())
            .catch((e: any) => {
                console.error(e.message);
            });
    }
    catch (e: any) {
        console.error(e.message);
    }
}

const playSound = debounce(_playSound, 1, {immediate: true});

/**
 * Release the shared context. Exported for tests; there is no teardown point in
 * the app, and the whole purpose of the context is to outlive any one sound.
 */
export function __resetAudioContext(): void {
    sharedContext?.close().catch(() => {});
    sharedContext = null;
}

export default playSound;
